(() => {
  const ELEMENT_NAME = 'vl-okendo-quiz-results';
  const BRIDGE_MARKER = '__vlOkendoQuizResultsBridge';
  const MAX_PRODUCTS = 3;
  const CLICK_FROM = {
    image: 'quiz_image',
    title: 'quiz_title'
  };

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const textValue = (template, values = {}) => Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`{${key}}`, String(value)),
    String(template || '')
  );

  const formatMoney = (value, currency) => {
    if (value == null || value === '') return '';
    const amount = Number(value) / 100;
    const hasFraction = Math.round(amount * 100) % 100 !== 0;
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency || window.Shopify?.currency?.active || 'USD',
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 2 : 0
    }).format(amount);
  };

  const handleFromProduct = (product) => {
    if (!product?.url) return '';
    try {
      const url = new URL(product.url, window.location.origin);
      return url.pathname.match(/\/products\/([^/?#]+)/)?.[1] || '';
    } catch (error) {
      return String(product.url).match(/\/products\/([^/?#]+)/)?.[1] || '';
    }
  };

  const remoteVariantId = (variant) => String(
    variant?.variantRemoteId || variant?.remoteId || variant?.id || ''
  ).replace(/^shopify-/, '');

  const productCardUrl = (product, variant) => (
    `/products/${encodeURIComponent(product.handle)}?variant=${encodeURIComponent(variant.id)}`
  );

  const withClickFrom = (url, clickFrom) => {
    if (!url || !clickFrom) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}click_from=${encodeURIComponent(clickFrom)}`;
  };

  const normalizeField = (name) => {
    const value = String(name || '').trim().toLowerCase();
    if (/colou?r/.test(value)) return 'color';
    if (/size/.test(value)) return 'size';
    if (/style|frame/.test(value)) return 'style';
    return value.replace(/[^a-z0-9]+/g, '_') || 'option';
  };

  const swatchBackground = (value, patterns = '') => {
    const colorName = String(value || '').trim();
    const configuredValue = String(patterns).split(/\r?\n/).reduce((match, pattern) => {
      const [label, ...parts] = pattern.split('::');
      return label?.trim().toLowerCase() === colorName.toLowerCase()
        ? parts.join('::').trim()
        : match;
    }, '');
    const swatchValue = configuredValue || colorName;

    if (/\.(png|jpe?g|webp)$/i.test(swatchValue)) {
      return `url("/cdn/shop/files/${encodeURIComponent(swatchValue)}")`;
    }

    const colors = swatchValue.split('/').map((color) => color.trim()).filter((color) => (
      window.CSS?.supports?.('color', color)
    ));
    if (colors.length > 1) {
      const step = 100 / (colors.length - 1);
      return `linear-gradient(44deg, ${colors.map((color, index) => `${color} ${index * step}%`).join(', ')})`;
    }
    return colors[0] || '#d9d9d9';
  };

  const starMarkup = (rating) => {
    const rounded = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    return Array.from({ length: 5 }, (_, index) => (
      `<svg class="${index < rounded ? 'is-filled' : ''}" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 1.25l1.96 3.97 4.38.64-3.17 3.09.75 4.36L8 11.25l-3.92 2.06.75-4.36-3.17-3.09 4.38-.64L8 1.25z"></path></svg>`
    )).join('');
  };

  const routeIcon = () => `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true" focusable="false">
      <path d="M7 8.45825C8.44975 8.45825 9.625 7.283 9.625 5.83325C9.625 4.3835 8.44975 3.20825 7 3.20825C5.55025 3.20825 4.375 4.3835 4.375 5.83325C4.375 7.283 5.55025 8.45825 7 8.45825Z" stroke="#1F1F1F" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M21 24.7917C22.4497 24.7917 23.625 23.6165 23.625 22.1667C23.625 20.717 22.4497 19.5417 21 19.5417C19.5503 19.5417 18.375 20.717 18.375 22.1667C18.375 23.6165 19.5503 24.7917 21 24.7917Z" stroke="#1F1F1F" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M12.1498 12.3499L12.35 12.1497C13.2612 11.2385 14.7387 11.2385 15.6498 12.1497L15.85 12.3499C16.7612 13.2611 16.7612 14.7385 15.85 15.6497L15.6498 15.8499C14.7387 16.7611 13.2612 16.7611 12.35 15.8499L12.1498 15.6497C11.2386 14.7385 11.2386 13.2611 12.1498 12.3499Z" stroke="#1F1F1F" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M9.9166 5.83325H19.5416C21.7968 5.83325 23.6249 7.66142 23.6249 9.91659C23.6249 12.1718 21.7968 13.9999 19.5416 13.9999H17.4999" stroke="#1F1F1F" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M10.5 14H8.45833C6.20317 14 4.375 15.8282 4.375 18.0833C4.375 20.3385 6.20317 22.1667 8.45833 22.1667H18.0833" stroke="#1F1F1F" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;

  const leafIcon = () => `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true" focusable="false">
      <path d="M5.54181 24.7916C5.54181 14.6349 9.31662 4.47821 20.3998 3.22169C21.7039 3.07385 22.7947 4.16836 22.3622 5.39166C21.0801 9.01918 17.2343 11.5305 17.2343 11.5305L18.3467 12.3477C19.1551 12.9416 19.489 13.9783 18.9906 14.8428C17.6786 17.1187 14.2764 21.1749 6.69053 21.1749" stroke="#1F1F1F" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;

  const chevronIcon = () => `
    <svg class="vl-okendo-quiz-results__option-chevron" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M5.519 6.462a.333.333 0 0 0 .471 0l5.421-5.422a.333.333 0 0 0 0-.471l-.471-.471a.333.333 0 0 0-.471 0L5.755 4.812 1.04.098a.333.333 0 0 0-.471 0L.098.569a.333.333 0 0 0 0 .471l5.421 5.422Z" transform="translate(2.245 4.5)"></path>
    </svg>
  `;

  const ensureBridge = () => {
    window.okeConnectApi = window.okeConnectApi || {};
    const api = window.okeConnectApi;
    if (api.recommendedProductsMounted?.[BRIDGE_MARKER]) return true;

    const previous = api.recommendedProductsMounted;
    const bridge = (recommendationSection, products) => {
      if (typeof previous === 'function') previous(recommendationSection, products);
      document.querySelectorAll(ELEMENT_NAME).forEach((instance) => {
        instance.handleRecommendedProductsMounted?.(recommendationSection, products);
      });
    };
    bridge[BRIDGE_MARKER] = true;
    api.recommendedProductsMounted = bridge;
    return true;
  };

  const ensureGlobalQuizTrigger = () => {
    if (window.vlGlobalOkendoQuizInitialized) return;
    window.vlGlobalOkendoQuizInitialized = true;

    const waitForApi = () => new Promise((resolve, reject) => {
      const startedAt = Date.now();
      const poll = () => {
        if (window.okeConnectApi && typeof window.okeConnectApi.showQuiz === 'function') {
          resolve(window.okeConnectApi);
          return;
        }
        if (Date.now() - startedAt >= 8000) {
          reject(new Error('Okendo Quiz API did not initialize.'));
          return;
        }
        window.setTimeout(poll, 50);
      };
      poll();
    });

    document.addEventListener('click', async (event) => {
      const trigger = event.target.closest('[data-vl-okendo-quiz-trigger], a[href="#quiz"], a[href$="#quiz"]');
      if (!trigger) return;

      const host = document.querySelector('[data-vl-global-okendo-quiz]');
      if (!host || host.dataset.vlOkendoLoading === 'true') return;

      event.preventDefault();
      event.stopPropagation();
      host.dataset.vlOkendoLoading = 'true';
      try {
        window.__vlOkendoQuizResultsActive = host;
        const config = JSON.parse(host.querySelector('[data-vl-okendo-quiz-results-config]')?.textContent || '{}');
        const api = await waitForApi();
        api.showQuiz(config.quizId, config.subscriberId);
      } catch (error) {
        console.error('[VL Okendo Quiz]', error);
      } finally {
        delete host.dataset.vlOkendoLoading;
      }
    }, true);
  };

  ensureBridge();
  ensureGlobalQuizTrigger();
  let bridgeAttempts = 0;
  const bridgeTimer = window.setInterval(() => {
    ensureBridge();
    bridgeAttempts += 1;
    if (bridgeAttempts >= 60) window.clearInterval(bridgeTimer);
  }, 500);

  if (customElements.get(ELEMENT_NAME)) return;

  class VLOkendoQuizResults extends HTMLElement {
    constructor() {
      super();
      this.config = {};
      this.products = [];
      this.selectedHandles = new Set();
      this.requestVersion = 0;
      this.abortController = null;
      this.recommendationSection = null;
      this.modalRoot = null;
      this.nativeRecommendationNodes = [];
      this.resultHeadingState = null;
      this.nativeRetakeButton = null;
      this.cardHeightFrame = 0;
      this.cardHeightObserver = null;
      this.variantImagePreloadCancel = null;
      this.variantImageLoaders = new Set();
      this.affirmRefreshTimer = 0;
      this.affirmRefreshAttempts = 0;
      this.handleChange = this.handleChange.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handleDocumentKeyDown = this.handleDocumentKeyDown.bind(this);
      this.scheduleMobileCardHeightSync = this.scheduleMobileCardHeightSync.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      try {
        this.config = JSON.parse(this.querySelector('[data-vl-okendo-quiz-results-config]')?.textContent || '{}');
      } catch (error) {
        this.config = {};
      }
      this.liveRegion = this.querySelector('[data-vl-okendo-quiz-results-live]');
      this.addEventListener('change', this.handleChange);
      this.addEventListener('click', this.handleClick);
      this.addEventListener('pointerdown', this.handlePointerDown);
      if (this.isModalMode()) {
        this.ownerDocument.addEventListener('change', this.handleChange);
        this.ownerDocument.addEventListener('click', this.handleClick);
        this.ownerDocument.addEventListener('pointerdown', this.handlePointerDown);
      }
      this.ownerDocument.addEventListener('keydown', this.handleDocumentKeyDown, true);
      window.addEventListener('resize', this.scheduleMobileCardHeightSync, { passive: true });
      ensureBridge();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('pointerdown', this.handlePointerDown);
      if (this.isModalMode()) {
        this.ownerDocument.removeEventListener('change', this.handleChange);
        this.ownerDocument.removeEventListener('click', this.handleClick);
        this.ownerDocument.removeEventListener('pointerdown', this.handlePointerDown);
      }
      this.ownerDocument.removeEventListener('keydown', this.handleDocumentKeyDown, true);
      window.removeEventListener('resize', this.scheduleMobileCardHeightSync);
      this.cardHeightObserver?.disconnect();
      window.cancelAnimationFrame(this.cardHeightFrame);
      this.variantImagePreloadCancel?.();
      this.variantImageLoaders.clear();
      window.clearTimeout(this.affirmRefreshTimer);
      if (this.nativeRetakeButton) this.nativeRetakeButton.hidden = false;
    }

    isModalMode() {
      return this.dataset.displayMode === 'modal';
    }

    ownsRecommendationSection(recommendationSection) {
      if (this.contains(recommendationSection)) return true;
      if (!this.isModalMode()) return false;
      if (window.__vlOkendoQuizResultsActive && window.__vlOkendoQuizResultsActive !== this) return false;
      return Boolean(recommendationSection.closest('.okeQuiz--popup'));
    }

    ownsInteractionTarget(target) {
      if (!(target instanceof Element)) return false;
      if (this.contains(target)) return true;
      return Boolean(this.isModalMode() && this.recommendationSection?.contains(target));
    }

    resultQuery(selector) {
      return this.recommendationSection?.querySelector(selector) || this.querySelector(selector);
    }

    resultQueryAll(selector) {
      const root = this.recommendationSection || this;
      return root.querySelectorAll(selector);
    }

    setState(state) {
      this.dataset.state = state;
      if (this.modalRoot) this.modalRoot.dataset.state = state;
    }

    async handleRecommendedProductsMounted(recommendationSection, products) {
      if (!(recommendationSection instanceof Element) || !this.ownsRecommendationSection(recommendationSection)) return;
      const normalized = this.normalizeRecommendations(products);
      if (!normalized.length) return;
      if (recommendationSection === this.recommendationSection && this.dataset.state === 'loading') return;

      const version = ++this.requestVersion;
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.recommendationSection = recommendationSection;
      if (this.isModalMode()) {
        this.modalRoot = recommendationSection.closest('.okeQuiz--popup');
        this.modalRoot?.classList.add('vl-okendo-quiz-results', 'vl-okendo-quiz-results--modal');
      }
      this.nativeRecommendationNodes = Array.from(recommendationSection.childNodes);
      this.captureResultHeading();
      this.setState('loading');
      this.setAttribute('aria-busy', 'true');
      this.announce(this.config.labels?.loading);
      this.configureResultHeading();
      this.renderLoading(normalized.length);

      try {
        const loaded = await Promise.all(normalized.map(async (recommendation) => {
          try {
            return await this.loadProduct(recommendation, this.abortController.signal);
          } catch (error) {
            if (error.name === 'AbortError') throw error;
            return null;
          }
        }));
        if (version !== this.requestVersion || !this.isConnected) return;
        const validProducts = loaded.filter(Boolean);
        if (!validProducts.length) throw new Error('No valid products were returned');

        this.products = validProducts;
        this.selectedHandles.clear();
        this.render();
        this.setState('ready');
        this.removeAttribute('aria-busy');
        this.dataset.productCount = String(validProducts.length);
        this.nativeRecommendationNodes = [];
        this.resultHeadingState = null;
        this.announce('');
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.restoreNativeResults();
        this.setState('native-fallback');
        this.removeAttribute('aria-busy');
        this.announce(this.config.labels?.error);
      }
    }

    normalizeRecommendations(products) {
      const seen = new Set();
      return (Array.isArray(products) ? products : []).reduce((result, product, index) => {
        const handle = handleFromProduct(product);
        if (!handle || seen.has(handle) || result.length >= MAX_PRODUCTS) return result;
        seen.add(handle);
        result.push({ product, handle, rank: index });
        return result;
      }, []);
    }

    async loadProduct(recommendation, signal) {
      const endpoint = String(this.config.productEndpoint || '')
        .replace('{handle}', encodeURIComponent(recommendation.handle));
      const response = await fetch(endpoint, {
        signal,
        cache: 'no-store',
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
      const data = await response.json();
      const product = data.product;
      if (!product || product.handle !== recommendation.handle) return null;

      const variants = (Array.isArray(product.variants) ? product.variants : []).filter((variant) => !variant.hidden);
      if (!variants.length) return null;
      product.variants = variants;

      const recommendedIds = new Set((recommendation.product?.variants || []).map(remoteVariantId).filter(Boolean));
      const defaultVariant = variants.find((variant) => recommendedIds.has(String(variant.id)) && variant.available)
        || variants.find((variant) => String(variant.id) === String(data.defaultVariantId))
        || variants.find((variant) => variant.available)
        || variants[0];

      const optionFields = (product.options || []).map(normalizeField);
      const selection = {};
      optionFields.forEach((field, index) => {
        selection[field] = defaultVariant.options?.[index] || '';
      });

      return {
        rank: recommendation.rank,
        okendo: recommendation.product,
        product,
        shortName: data.shortName || product.title,
        selectorSubtitle: data.selectorSubtitle || '',
        specs: data.specs || {},
        ratingAverage: Number(data.ratingAverage || recommendation.product?.averageRating || 0),
        reviewCount: Number(data.reviewCount || recommendation.product?.approvedReviewsCount || 0),
        optionFields,
        selection,
        variant: defaultVariant,
        swatchPatterns: this.config.swatchPatterns || ''
      };
    }

    optionValues(productState, optionIndex) {
      const currentField = productState.optionFields[optionIndex];
      const otherFields = productState.optionFields.filter((field) => field !== currentField);
      const values = productState.product.variants
        .filter((variant) => otherFields.every((field) => {
          const index = productState.optionFields.indexOf(field);
          return !productState.selection[field] || variant.options?.[index] === productState.selection[field];
        }))
        .map((variant) => variant.options?.[optionIndex])
        .filter(Boolean);
      const unique = [...new Set(values)];
      if (unique.length) return unique;
      return [...new Set(productState.product.variants.map((variant) => variant.options?.[optionIndex]).filter(Boolean))];
    }

    optionValueAvailable(productState, optionIndex, value) {
      return productState.product.variants.some((variant) => (
        variant.available
        && variant.options?.[optionIndex] === value
        && productState.optionFields.every((field, index) => (
          index === optionIndex
          || !productState.selection[field]
          || variant.options?.[index] === productState.selection[field]
        ))
      ));
    }

    updateVariant(productState, field, value) {
      productState.selection[field] = value;
      const fieldIndex = productState.optionFields.indexOf(field);
      const candidates = productState.product.variants.filter((variant) => variant.options?.[fieldIndex] === value);
      const scored = candidates.map((variant) => ({
        variant,
        score: productState.optionFields.reduce((score, optionField, index) => (
          score + (variant.options?.[index] === productState.selection[optionField] ? 1 : 0)
        ), 0) * 10 + (variant.available ? 1 : 0)
      })).sort((a, b) => b.score - a.score);
      const nextVariant = scored[0]?.variant || productState.product.variants.find((variant) => variant.available) || productState.product.variants[0];
      if (!nextVariant) return;
      productState.variant = nextVariant;
      productState.optionFields.forEach((optionField, index) => {
        productState.selection[optionField] = nextVariant.options?.[index] || '';
      });
    }

    render() {
      this.preloadVariantImages();
      const container = document.createElement('div');
      container.className = 'vl-okendo-quiz-results__custom';
      container.dataset.vlOkendoQuizResultsCustom = '';
      container.innerHTML = `
        <div class="vl-okendo-quiz-results__cards">
          ${this.products[0] ? this.renderCard(this.products[0], true) : ''}
          ${this.products.length > 1 ? `
            <div class="vl-okendo-quiz-results__secondary-track" aria-label="${escapeHtml(this.config.labels?.additionalRecommendations)}">
              ${this.products.slice(1).map((product) => this.renderCard(product, false)).join('')}
            </div>
          ` : ''}
        </div>
        ${this.renderActions()}
      `;
      this.recommendationSection.replaceChildren(container);
      this.observeMobileCardHeights(container);
      this.findNativeRetakeButton();
      this.refreshAffirm();
    }

    renderLoading(productCount) {
      const secondaryCount = Math.max(0, Math.min(MAX_PRODUCTS - 1, productCount - 1));
      const skeletonCard = (primary) => `
        <article class="vl-okendo-quiz-results__card ${primary ? 'is-primary' : 'is-secondary'} is-loading-card">
          <div class="vl-okendo-quiz-results__media">
            <span class="vl-okendo-quiz-results__loading-visual"></span>
            <span class="vl-okendo-quiz-results__loading-swatches"></span>
          </div>
          <div class="vl-okendo-quiz-results__content">
            <div class="vl-okendo-quiz-results__loading-lines">
              <span></span><span></span><span></span><span></span><span></span>
            </div>
          </div>
        </article>
      `;
      const container = document.createElement('div');
      container.className = 'vl-okendo-quiz-results__custom is-loading';
      container.dataset.vlOkendoQuizResultsLoading = '';
      container.setAttribute('aria-hidden', 'true');
      container.innerHTML = `
        <div class="vl-okendo-quiz-results__cards">
          ${skeletonCard(true)}
          ${secondaryCount ? `
            <div class="vl-okendo-quiz-results__secondary-track">
              ${Array.from({ length: secondaryCount }, () => skeletonCard(false)).join('')}
            </div>
          ` : ''}
        </div>
        <div class="vl-okendo-quiz-results__loading-actions"></div>
      `;
      this.recommendationSection.replaceChildren(container);
    }

    observeMobileCardHeights(container) {
      this.cardHeightObserver?.disconnect();
      this.cardHeightObserver = new MutationObserver(this.scheduleMobileCardHeightSync);
      this.cardHeightObserver.observe(container, {
        childList: true,
        characterData: true,
        subtree: true
      });
      this.scheduleMobileCardHeightSync();
      document.fonts?.ready.then(() => {
        if (this.isConnected) this.scheduleMobileCardHeightSync();
      });
    }

    scheduleMobileCardHeightSync() {
      window.cancelAnimationFrame(this.cardHeightFrame);
      this.cardHeightFrame = window.requestAnimationFrame(() => this.syncMobileCardHeights());
    }

    syncMobileCardHeights() {
      const container = this.resultQuery('[data-vl-okendo-quiz-results-custom]');
      if (!container) return;
      container.style.removeProperty('--vl-okendo-mobile-card-height');
      if (!window.matchMedia('(max-width: 767px)').matches) return;

      const cards = Array.from(container.querySelectorAll('.vl-okendo-quiz-results__card'));
      if (!cards.length) return;
      const tallestHeight = Math.max(...cards.map((card) => Math.ceil(card.getBoundingClientRect().height)));
      container.style.setProperty('--vl-okendo-mobile-card-height', `${tallestHeight}px`);
    }

    updateProductCard(productState) {
      const card = Array.from(this.resultQueryAll('.vl-okendo-quiz-results__card')).find((candidate) => (
        candidate.dataset.productHandle === productState.product.handle
      ));
      if (!card) return;

      const { product, variant } = productState;
      this.updateProductImage(card, productState);

      const options = card.querySelector('.vl-okendo-quiz-results__options');
      if (options) options.innerHTML = this.renderChoiceOptions(productState);

      const media = card.querySelector('.vl-okendo-quiz-results__media');
      const currentColors = media?.querySelector('.vl-okendo-quiz-results__colors');
      const colorMarkup = this.renderColorOptions(productState);
      if (media && colorMarkup) {
        const template = document.createElement('template');
        template.innerHTML = colorMarkup.trim();
        const nextColors = template.content.firstElementChild;
        if (currentColors && nextColors) currentColors.replaceWith(nextColors);
        else if (nextColors) media.append(nextColors);
      } else {
        currentColors?.remove();
      }

      const currentPrice = formatMoney(variant.price, productState.okendo?.currencyCode);
      const comparePrice = Number(variant.compareAtPrice || 0) > Number(variant.price || 0)
        ? formatMoney(variant.compareAtPrice, productState.okendo?.currencyCode)
        : '';
      const price = card.querySelector('.vl-okendo-quiz-results__price');
      const priceValue = price?.querySelector('span');
      if (priceValue) priceValue.textContent = currentPrice;
      let comparePriceElement = price?.querySelector('s');
      if (comparePrice) {
        if (!comparePriceElement && price) {
          comparePriceElement = document.createElement('s');
          price.append(comparePriceElement);
        }
        comparePriceElement.textContent = comparePrice;
      } else {
        comparePriceElement?.remove();
      }

      const financing = card.querySelector('.vl-okendo-quiz-results__financing');
      const amountChanged = financing && financing.dataset.amount !== String(variant.price || '');
      if (financing && variant.price) financing.dataset.amount = String(variant.price);

      const commerceDetails = price?.parentElement;
      let availability = commerceDetails?.querySelector('.vl-okendo-quiz-results__availability');
      if (!variant.available && !availability && commerceDetails) {
        availability = document.createElement('p');
        availability.className = 'vl-okendo-quiz-results__availability';
        availability.textContent = this.config.labels?.soldOut || '';
        commerceDetails.append(availability);
      } else if (variant.available) {
        availability?.remove();
      }

      const productUrl = productCardUrl(product, variant);
      const mediaLink = card.querySelector('.vl-okendo-quiz-results__media-link');
      if (mediaLink) mediaLink.href = withClickFrom(productUrl, CLICK_FROM.image);
      const titleLink = card.querySelector('.vl-okendo-quiz-results__title-link');
      if (titleLink) titleLink.href = withClickFrom(productUrl, CLICK_FROM.title);

      const variantInput = card.querySelector('[data-product-form-variant]');
      if (variantInput) variantInput.value = variant.id;

      const addToCart = card.querySelector('.vl-okendo-quiz-results__add-to-cart');
      if (addToCart) {
        const buttonLabel = variant.available ? this.config.labels?.addToCart : this.config.labels?.soldOut;
        addToCart.toggleAttribute('disabled', !variant.available);
        addToCart.setAttribute('aria-label', buttonLabel || '');
        const buttonText = addToCart.querySelector('[data-button-text]');
        if (buttonText) buttonText.textContent = buttonLabel || '';
      }

      if (amountChanged) this.refreshAffirm();
    }

    updateProductImage(card, productState) {
      const { product, variant } = productState;
      const fullImage = variant.image || product.featuredImage || productState.okendo?.imageUrl || '';
      const imageElement = card.querySelector('.vl-okendo-quiz-results__media-link > img');
      if (!imageElement || !fullImage) return;

      const requestId = String(Number(imageElement.dataset.imageRequestId || 0) + 1);
      imageElement.dataset.imageRequestId = requestId;
      imageElement.dataset.pendingFullSrc = fullImage;
      imageElement.setAttribute('aria-busy', 'true');

      const isCurrentRequest = () => (
        imageElement.dataset.imageRequestId === requestId
        && imageElement.dataset.pendingFullSrc === fullImage
      );
      const loadImage = (source) => new Promise((resolve, reject) => {
        const loader = new Image();
        loader.decoding = 'async';
        loader.onload = async () => {
          try {
            await loader.decode?.();
          } catch (error) {
            // The image is still usable when decode() is unsupported or rejects.
          }
          resolve(source);
        };
        loader.onerror = reject;
        loader.src = source;
      });

      const currentFullImage = imageElement.dataset.fullSrc || imageElement.getAttribute('src') || '';
      const currentSource = imageElement.getAttribute('src') || '';
      if (currentFullImage === fullImage && currentSource === fullImage) {
        delete imageElement.dataset.pendingFullSrc;
        imageElement.removeAttribute('aria-busy');
        return;
      }

      loadImage(fullImage).then(() => {
        if (!isCurrentRequest()) return;
        imageElement.src = fullImage;
        imageElement.dataset.fullSrc = fullImage;
        delete imageElement.dataset.pendingFullSrc;
        imageElement.removeAttribute('aria-busy');
      }).catch(() => {
        if (!isCurrentRequest()) return;
        delete imageElement.dataset.pendingFullSrc;
        imageElement.removeAttribute('aria-busy');
      });
    }

    preloadVariantImages() {
      this.variantImagePreloadCancel?.();
      const imageUrls = new Set(this.products.flatMap(({ product }) => (
        product.variants.map((variant) => variant.image).filter(Boolean)
      )));
      if (!imageUrls.size) return;

      const preload = () => {
        this.variantImagePreloadCancel = null;
        imageUrls.forEach((source) => {
          const loader = new Image();
          loader.decoding = 'async';
          loader.fetchPriority = 'high';
          const release = () => this.variantImageLoaders.delete(loader);
          loader.onload = release;
          loader.onerror = release;
          this.variantImageLoaders.add(loader);
          loader.src = source;
        });
      };

      preload();
    }

    updateCompareControls(compare) {
      compare.closest('.vl-okendo-quiz-results__compare')?.classList.toggle('is-selected', compare.checked);
      const submit = this.resultQuery('[data-vl-okendo-quiz-compare-submit]');
      if (submit) submit.disabled = this.selectedHandles.size < 2;
    }

    configureResultHeading() {
      const resultBlock = this.recommendationSection?.parentElement;
      if (!resultBlock) return;
      const heading = Array.from(resultBlock.children).find((element) => (
        element.getAttribute?.('role') === 'heading' && element.getAttribute('aria-level') === '1'
      ));
      if (!heading) return;

      if (this.config.heading) heading.textContent = this.config.heading;
      const description = heading.nextElementSibling?.tagName === 'P' ? heading.nextElementSibling : null;
      if (description) {
        description.textContent = this.config.description || '';
        description.hidden = !this.config.description;
      }
      heading.tabIndex = -1;
      window.requestAnimationFrame(() => heading.focus({ preventScroll: true }));
    }

    captureResultHeading() {
      const resultBlock = this.recommendationSection?.parentElement;
      if (!resultBlock) return;
      const heading = Array.from(resultBlock.children).find((element) => (
        element.getAttribute?.('role') === 'heading' && element.getAttribute('aria-level') === '1'
      ));
      if (!heading) return;
      const description = heading.nextElementSibling?.tagName === 'P' ? heading.nextElementSibling : null;
      this.resultHeadingState = {
        heading,
        headingText: heading.textContent,
        headingTabIndex: heading.getAttribute('tabindex'),
        description,
        descriptionText: description?.textContent || '',
        descriptionHidden: description?.hidden || false
      };
    }

    restoreNativeResults() {
      if (this.recommendationSection && this.nativeRecommendationNodes.length) {
        this.recommendationSection.replaceChildren(...this.nativeRecommendationNodes);
      }
      const state = this.resultHeadingState;
      if (state?.heading) {
        state.heading.textContent = state.headingText;
        if (state.headingTabIndex == null) state.heading.removeAttribute('tabindex');
        else state.heading.setAttribute('tabindex', state.headingTabIndex);
      }
      if (state?.description) {
        state.description.textContent = state.descriptionText;
        state.description.hidden = state.descriptionHidden;
      }
      this.nativeRecommendationNodes = [];
      this.resultHeadingState = null;
    }

    renderCard(productState, primary) {
      const { product, variant } = productState;
      const image = variant.image || product.featuredImage || productState.okendo?.imageUrl || '';
      const currentPrice = formatMoney(variant.price, productState.okendo?.currencyCode);
      const comparePrice = Number(variant.compareAtPrice || 0) > Number(variant.price || 0)
        ? formatMoney(variant.compareAtPrice, productState.okendo?.currencyCode)
        : '';
      const url = productCardUrl(product, variant);
      const imageUrl = withClickFrom(url, CLICK_FROM.image);
      const titleUrl = withClickFrom(url, CLICK_FROM.title);
      const reasons = this.reasonValues(productState);
      const selected = this.selectedHandles.has(product.handle);

      return `
        <article class="vl-okendo-quiz-results__card ${primary ? 'is-primary' : 'is-secondary'}" data-product-handle="${escapeHtml(product.handle)}">
          <div class="vl-okendo-quiz-results__media">
            ${primary ? `<span class="vl-okendo-quiz-results__best-match"><span aria-hidden="true">★</span>${escapeHtml(this.config.labels?.bestMatch)}</span>` : ''}
            ${image ? `<a class="vl-okendo-quiz-results__media-link" href="${escapeHtml(imageUrl)}" aria-label="${escapeHtml(product.title)}"><img src="${escapeHtml(image)}" data-full-src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="eager" decoding="async" fetchpriority="high"></a>` : ''}
            ${this.renderColorOptions(productState)}
          </div>
          ${this.config.showCompare ? `
            <label class="vl-okendo-quiz-results__compare ${selected ? 'is-selected' : ''}">
              <input type="checkbox" data-vl-okendo-quiz-compare value="${escapeHtml(product.handle)}" ${selected ? 'checked' : ''}>
              <span aria-hidden="true">
                <svg class="vl-okendo-quiz-results__compare-check" viewBox="0 0 12 12" focusable="false">
                  <path fill="#000" fill-opacity=".85" fill-rule="evenodd" clip-rule="evenodd" d="M10.9199 2.20403C11.3597 2.64311 11.3601 3.35541 10.9208 3.79502L4.92571 9.79503C4.48667 10.2344 3.77451 10.2351 3.33468 9.79657L1.08065 7.55174C0.640604 7.11297 0.639639 6.40061 1.07862 5.96071C1.51757 5.52084 2.23023 5.51988 2.67029 5.95865L4.12799 7.40998L9.32827 2.20497C9.76753 1.76538 10.4801 1.76496 10.9199 2.20403Z"/>
                </svg>
              </span>
              ${escapeHtml(this.config.labels?.compare)}
            </label>
          ` : ''}
          <div class="vl-okendo-quiz-results__content">
            <div class="vl-okendo-quiz-results__product-details">
              <div class="vl-okendo-quiz-results__summary">
                ${this.renderRating(productState)}
                <h3><a class="vl-okendo-quiz-results__title-link" href="${escapeHtml(titleUrl)}">${escapeHtml(product.title)}</a></h3>
                <div class="vl-okendo-quiz-results__options">${this.renderChoiceOptions(productState)}</div>
              </div>
              <div class="vl-okendo-quiz-results__commerce">
                <div>
                  <div class="vl-okendo-quiz-results__price">
                    <span>${escapeHtml(currentPrice)}</span>
                    ${comparePrice ? `<s>${escapeHtml(comparePrice)}</s>` : ''}
                  </div>
                  ${variant.price ? `<p class="vl-okendo-quiz-results__financing affirm-as-low-as" data-page-type="product" data-amount="${escapeHtml(variant.price)}"></p>` : ''}
                  ${variant.available ? '' : `<p class="vl-okendo-quiz-results__availability">${escapeHtml(this.config.labels?.soldOut)}</p>`}
                </div>
                <product-form class="vl-okendo-quiz-results__product-form" data-product-id="${escapeHtml(product.id)}">
                  <form method="post" action="/cart/add" accept-charset="UTF-8" enctype="multipart/form-data" data-type="add-to-cart-form">
                    <input type="hidden" name="id" value="${escapeHtml(variant.id)}" data-product-form-variant>
                    <input type="hidden" name="quantity" value="1">
                    <button type="submit" name="add" class="vl-okendo-quiz-results__add-to-cart" aria-label="${escapeHtml(variant.available ? this.config.labels?.addToCart : this.config.labels?.soldOut)}" ${variant.available ? '' : 'disabled'}>
                      <span data-button-text>${escapeHtml(variant.available ? this.config.labels?.addToCart : this.config.labels?.soldOut)}</span>
                      <span class="loading-spinner btn__loading-spinner" aria-hidden="true"></span>
                    </button>
                  </form>
                </product-form>
              </div>
            </div>
            ${reasons.length ? `
              <div class="vl-okendo-quiz-results__reasons">
                ${reasons.map((reason, index) => `${index > 0 ? '<div class="vl-okendo-quiz-results__reason-divider" aria-hidden="true"></div>' : ''}
                  <div class="vl-okendo-quiz-results__reason ${index > 0 ? 'is-secondary-reason' : ''}">
                    <span>${index === 0 ? routeIcon() : leafIcon()}</span>
                    <div class="vl-okendo-quiz-results__reason-text">
                      <p class="vl-okendo-quiz-results__reason-title">${escapeHtml(reason.title)}</p>
                      <p class="vl-okendo-quiz-results__reason-copy">${escapeHtml(reason.value)}</p>
                    </div>
                  </div>
                `).join('')}
              </div>
            ` : ''}
          </div>
        </article>
      `;
    }

    renderRating(productState) {
      if (!productState.reviewCount) return '';
      const reviewLabel = textValue(this.config.labels?.reviews, { count: productState.reviewCount });
      return `
        <div class="vl-okendo-quiz-results__rating" aria-label="${escapeHtml(`${productState.ratingAverage} out of 5, ${reviewLabel}`)}">
          <span class="vl-okendo-quiz-results__stars">${starMarkup(productState.ratingAverage)}</span>
          <span>${escapeHtml(reviewLabel)}</span>
        </div>
      `;
    }

    renderChoiceOptions(productState) {
      return productState.optionFields.map((field, index) => {
        if (field === 'color') return '';
        const values = this.optionValues(productState, index);
        if (!values.length) return '';
        if (values.length === 1) return `<span class="vl-okendo-quiz-results__single-option">${escapeHtml(values[0])}</span>`;
        return `
          <label class="vl-okendo-quiz-results__option-select">
            <span class="visually-hidden">${escapeHtml(productState.product.options[index] || field)}</span>
            <span class="vl-okendo-quiz-results__option-value" aria-hidden="true">${escapeHtml(productState.selection[field])}</span>
            ${chevronIcon()}
            <select data-vl-okendo-quiz-option data-product-handle="${escapeHtml(productState.product.handle)}" data-option-field="${escapeHtml(field)}">
              ${values.map((value) => `<option value="${escapeHtml(value)}" ${value === productState.selection[field] ? 'selected' : ''}>${escapeHtml(value)}</option>`).join('')}
            </select>
          </label>
        `;
      }).join('');
    }

    renderColorOptions(productState) {
      const colorIndex = productState.optionFields.indexOf('color');
      if (colorIndex < 0) return '';
      const values = this.optionValues(productState, colorIndex);
      if (!values.length) return '';
      let desktopColumns = Math.min(values.length, 5);
      if (values.length >= 6 && values.length <= 8) desktopColumns = 4;
      else if (values.length > 8 && values.length % 5 === 1) desktopColumns = 4;
      const desktopWidth = (desktopColumns * 20) + (Math.max(0, desktopColumns - 1) * 16);
      const mobileWidth = (values.length * 16) + (Math.max(0, values.length - 1) * 16);
      return `
        <fieldset
          class="vl-okendo-quiz-results__colors ${values.length > 5 ? 'has-multiple-rows' : ''}"
          data-swatch-count="${values.length}"
          style="--vl-okendo-swatch-desktop-width:${desktopWidth}px;--vl-okendo-swatch-mobile-width:${mobileWidth}px"
        >
          <legend class="visually-hidden">${escapeHtml(productState.product.options[colorIndex] || 'Color')}</legend>
          ${values.map((value) => {
            const available = this.optionValueAvailable(productState, colorIndex, value);
            return `
            <label class="${available ? '' : 'is-unavailable'}" title="${escapeHtml(value)}">
              <input type="radio" name="quiz-color-${escapeHtml(productState.product.id)}" value="${escapeHtml(value)}" data-vl-okendo-quiz-option data-product-handle="${escapeHtml(productState.product.handle)}" data-option-field="color" ${value === productState.selection.color ? 'checked' : ''}>
              <span style="--vl-okendo-quiz-swatch:${escapeHtml(swatchBackground(value, productState.swatchPatterns))}"></span>
              <span class="visually-hidden">${escapeHtml(value)}${available ? '' : `, ${escapeHtml(this.config.labels?.soldOut)}`}</span>
            </label>
          `;
          }).join('')}
        </fieldset>
      `;
    }

    reasonValues(productState) {
      const [primaryKey, secondaryKey] = this.config.reasonKeys || [];
      const primary = productState.specs?.[primaryKey] || productState.selectorSubtitle;
      const secondary = productState.specs?.[secondaryKey];
      return [
        { title: this.config.labels?.reasonPrimary, value: primary },
        { title: this.config.labels?.reasonSecondary, value: secondary }
      ].map((reason) => ({
        title: String(reason.title || '').trim(),
        value: String(reason.value || '').trim()
      })).filter((reason) => reason.value);
    }

    renderActions() {
      const selectedCount = this.selectedHandles.size;
      const compareDisabled = selectedCount < 2;
      return `
        <div class="vl-okendo-quiz-results__actions">
          ${this.config.showCompare ? `
            <button type="button" class="vl-okendo-quiz-results__compare-button" data-vl-okendo-quiz-compare-submit ${compareDisabled ? 'disabled' : ''}>
              ${escapeHtml(this.config.labels?.compareProducts)}
            </button>
          ` : ''}
          <button type="button" class="vl-okendo-quiz-results__reselect-button" data-vl-okendo-quiz-reselect hidden>
            ${escapeHtml(this.config.labels?.reselect)}
          </button>
        </div>
      `;
    }

    findNativeRetakeButton() {
      const label = String(this.config.labels?.nativeRetake || '').trim();
      const buttons = Array.from((this.modalRoot || this).querySelectorAll('button'));
      const button = buttons.find((candidate) => candidate.textContent?.trim() === label);
      const proxy = this.resultQuery('[data-vl-okendo-quiz-reselect]');
      if (!button || !proxy) return;
      this.nativeRetakeButton = button;
      button.hidden = true;
      proxy.hidden = false;
    }

    handleChange(event) {
      if (!this.ownsInteractionTarget(event.target)) return;
      const option = event.target.closest('[data-vl-okendo-quiz-option]');
      if (option) {
        const productState = this.products.find((candidate) => candidate.product.handle === option.dataset.productHandle);
        if (!productState) return;
        this.updateVariant(productState, option.dataset.optionField, option.value);
        this.updateProductCard(productState);
        return;
      }

      const compare = event.target.closest('[data-vl-okendo-quiz-compare]');
      if (!compare) return;
      if (compare.checked) this.selectedHandles.add(compare.value);
      else this.selectedHandles.delete(compare.value);
      this.updateCompareControls(compare);
      const selectedCount = this.selectedHandles.size;
      this.announce(selectedCount
        ? textValue(this.config.labels?.selectedCount, { count: selectedCount })
        : this.config.labels?.compareMinimum);
    }

    handlePointerDown(event) {
      if (!this.ownsInteractionTarget(event.target)) return;
      const optionSelect = event.target.closest('.vl-okendo-quiz-results__option-select');
      if (!optionSelect) return;
      optionSelect.classList.add('is-pointer-focus');
    }

    handleDocumentKeyDown() {
      this.resultQueryAll('.vl-okendo-quiz-results__option-select.is-pointer-focus').forEach((optionSelect) => {
        optionSelect.classList.remove('is-pointer-focus');
      });
    }

    handleClick(event) {
      if (!this.ownsInteractionTarget(event.target)) return;
      if (event.target.closest('[data-vl-okendo-quiz-compare-submit]')) {
        if (this.selectedHandles.size < 2) return;
        const handles = this.products
          .map((productState) => productState.product.handle)
          .filter((handle) => this.selectedHandles.has(handle));
        const url = new URL(this.config.comparePage || '/pages/compare-velotric-ebikes', window.location.origin);
        url.searchParams.set('products', handles.join(','));
        window.location.assign(`${url.pathname}${url.search}${url.hash}`);
        return;
      }

      if (event.target.closest('[data-vl-okendo-quiz-reselect]') && this.nativeRetakeButton) {
        this.selectedHandles.clear();
        this.nativeRetakeButton.hidden = false;
        this.nativeRetakeButton.click();
        this.nativeRetakeButton = null;
        this.setState('quiz-active');
        this.announce('');
      }
    }

    refreshAffirm() {
      const affirm = window.affirm;
      const financingMessages = this.resultQueryAll('.affirm-as-low-as');
      if (!financingMessages.length) return;

      window.clearTimeout(this.affirmRefreshTimer);
      if (!affirm?.ui) {
        if (this.affirmRefreshAttempts >= 40) {
          this.dataset.affirmState = 'unavailable';
          return;
        }
        this.affirmRefreshAttempts += 1;
        this.affirmRefreshTimer = window.setTimeout(() => this.refreshAffirm(), 250);
        return;
      }

      const refresh = () => {
        const currentUi = window.affirm?.ui;
        if (typeof currentUi?.refresh === 'function') {
          currentUi.refresh();
          this.affirmRefreshAttempts = 0;
          delete this.dataset.affirmState;
          return;
        }

        if (this.affirmRefreshAttempts < 40) {
          this.affirmRefreshAttempts += 1;
          this.affirmRefreshTimer = window.setTimeout(() => this.refreshAffirm(), 250);
        }
      };
      try {
        if (typeof affirm.ui.ready === 'function') affirm.ui.ready(refresh);
        else refresh();
      } catch (error) {
        if (this.affirmRefreshAttempts < 40) {
          this.affirmRefreshAttempts += 1;
          this.affirmRefreshTimer = window.setTimeout(() => this.refreshAffirm(), 250);
        } else {
          this.dataset.affirmState = 'unavailable';
        }
      }
    }

    announce(message) {
      if (this.liveRegion) this.liveRegion.textContent = String(message || '');
    }
  }

  customElements.define(ELEMENT_NAME, VLOkendoQuizResults);
})();
